import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckSquare, ChevronLeft, ChevronRight, Facebook, Linkedin, MessageCircle, Phone, Smartphone } from "lucide-react";
import { http, formatPrice, coverOf, imgUrl } from "@/lib/api";
import { ContactForm } from "@/components/site/ContactForm";
import { PageTitle } from "@/components/site/PageTitle";

const Feat = ({ children, testid }) => (
  <li data-testid={testid} className="flex items-center gap-3 py-1.5 border-b border-gray-300 text-[16px]">
    <CheckSquare className="w-4 h-4 text-gray-400" /> <span>{children}</span>
  </li>
);

const Gallery = ({ imgs, alt }) => {
  const [i, setI] = useState(0);
  const n = imgs.length;
  return (
    <div data-testid="detail-gallery">
      <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
        <img src={imgs[i]} alt={alt} className="w-full h-full object-cover" data-testid="detail-main-image" />
        {n > 1 && (
          <>
            <button aria-label="Precedente" onClick={() => setI((x) => (x - 1 + n) % n)} data-testid="gallery-prev"
              className="no-print absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center bg-black/35 text-white hover:bg-black/55"><ChevronLeft /></button>
            <button aria-label="Successiva" onClick={() => setI((x) => (x + 1) % n)} data-testid="gallery-next"
              className="no-print absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center bg-black/35 text-white hover:bg-black/55"><ChevronRight /></button>
            <span className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1">{i + 1} / {n}</span>
          </>
        )}
      </div>
      {n > 1 && (
        <div className="no-print mt-2 grid grid-cols-4 sm:grid-cols-8 gap-1.5">
          {imgs.map((src, k) => (
            <button key={src} onClick={() => setI(k)} data-testid={`detail-thumb-${k}`} className={`aspect-[4/3] overflow-hidden border-2 ${k === i ? "border-[var(--iv)]" : "border-transparent opacity-75 hover:opacity-100"}`}>
              <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ListingDetail() {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    http.get(`/listings/${id}`).then((r) => { setL(r.data); setState("ok"); }).catch(() => setState("error"));
  }, [id]);

  if (state === "loading") return <div className="py-32 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--iv)]" /></div>;
  if (state === "error" || !l) return (
    <div data-testid="listing-not-found" className="py-24 text-center">
      <p className="font-tit font-bold text-[var(--iv)] text-3xl uppercase">Immobile non disponibile</p>
      <Link to="/immobili" className="mt-4 inline-block underline">Torna alla ricerca</Link>
    </div>
  );

  const imgs = Array.isArray(l.immagini) && l.immagini.length ? l.immagini.map(imgUrl) : [coverOf(l)];
  const share = encodeURIComponent(window.location.href);

  return (
    <div data-testid="listing-detail-page">
      <PageTitle title={l.titolo} crumbs={[{ label: "Ricerca", to: "/immobili" }, { label: "Dettaglio immobile" }]} />
      <div className="max-w-[1240px] mx-auto px-4 pt-10 grid grid-cols-1 lg:grid-cols-[295px_1fr] gap-5">
        <aside className="order-2 lg:order-1">
          <div className="flex items-center justify-between">
            <a href="#contattaci" className="bg-[var(--iv)] text-white font-bold px-3 py-2 rounded-[3px] hover:bg-[var(--iv-dark)]" data-testid="detail-contattaci-button">Contattaci</a>
            <p data-testid="detail-price" className="text-[28px] text-[var(--iv)]">{formatPrice(l)}</p>
          </div>
          <ul className="mt-5">
            <Feat testid="detail-categoria"><b>{l.categoria}</b></Feat>
            <Feat testid="detail-contratto"><b>{l.contratto}</b></Feat>
            {l.mq ? <Feat testid="detail-mq"><b>{l.mq}</b> Mq</Feat> : null}
            {l.vani ? <Feat testid="detail-vani"><b>{l.vani}</b> Vani</Feat> : null}
            {l.camere ? <Feat testid="detail-camere"><b>{l.camere}</b> Camere</Feat> : null}
            {l.bagni ? <Feat testid="detail-bagni"><b>{l.bagni}</b> Bagni</Feat> : null}
          </ul>
          <div id="contattaci" className="mt-10 px-2.5 no-print">
            <h2 className="font-tit font-bold text-[var(--iv)] uppercase text-xl mb-3">Contattaci</h2>
            <ContactForm listing={l} compact />
          </div>
          <div className="mt-10 text-[15px] leading-7">
            <p className="font-bold">IVCASA CAMPO DI MARTE</p>
            <p>Via Francesco De Sanctis 32/34r</p>
            <p>50136 FIRENZE (FI)</p>
            <a href="tel:0559367109" className="flex items-center gap-2 text-[var(--iv)] text-lg"><Phone className="w-4 h-4 text-[#222]" /> 055 9367109</a>
            <a href="tel:3277816874" className="flex items-center gap-2 text-[var(--iv)] text-lg"><Smartphone className="w-4 h-4 text-[#222]" /> 327 7816874</a>
          </div>
        </aside>
        <div className="order-1 lg:order-2 min-w-0">
          <Gallery imgs={imgs} alt={l.titolo} />
          <p className="text-right text-[var(--iv)] mt-3 pr-2">Rif: <b data-testid="detail-rif">{l.riferimento}</b></p>
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-300 pb-6">
            <div>
              <h2 className="font-tit font-bold text-[var(--iv)] uppercase text-2xl">{l.comune}</h2>
              <p className="uppercase-first">{l.comune.toUpperCase()}{l.zona ? ` - zona ${l.zona}` : ""}</p>
            </div>
            <div className="text-right no-print">
              <button onClick={() => window.print()} className="bg-[var(--iv)] text-white text-sm font-bold px-3 py-1.5 rounded-[3px]" data-testid="detail-print-button">Stampa</button>
              <div className="flex items-center gap-2 mt-3 text-[15px]">
                Condividi
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${share}`} target="_blank" rel="noreferrer" aria-label="Facebook"><Facebook className="w-4 h-4 text-gray-500 hover:text-[var(--iv)]" /></a>
                <a href={`https://twitter.com/intent/tweet?url=${share}`} target="_blank" rel="noreferrer" aria-label="X" className="text-gray-500 hover:text-[var(--iv)] font-bold text-sm">X</a>
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${share}`} target="_blank" rel="noreferrer" aria-label="LinkedIn"><Linkedin className="w-4 h-4 text-gray-500 hover:text-[var(--iv)]" /></a>
                <a href={`https://wa.me/?text=${share}`} target="_blank" rel="noreferrer" aria-label="WhatsApp"><MessageCircle className="w-4 h-4 text-gray-500 hover:text-[var(--iv)]" /></a>
              </div>
            </div>
          </div>
          <p data-testid="detail-description" className="mt-8 leading-7 whitespace-pre-line border-b border-gray-300 pb-8">{l.descrizione || "Descrizione disponibile su richiesta."}</p>
          {l.mq ? (
            <div className="mt-8">
              <h3 className="font-tit font-bold text-[var(--iv)] uppercase text-lg mb-2">Consistenze</h3>
              <table className="w-full text-[16px]">
                <thead><tr className="border-b border-gray-300 text-left"><th className="py-1.5">Descrizione</th><th className="text-right">Superficie</th></tr></thead>
                <tbody><tr><td className="py-3 font-bold">Sup. Principale</td><td className="text-right">{l.mq} Mq</td></tr></tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
